import React, { forwardRef, useCallback, useEffect, useImperativeHandle } from "react";

import { IFlight, IGroundEvent } from "@vmosolutions/oc-interfaces";
import { useDispatch, useSelector } from "react-redux";
import { FlightChip } from "src/components/Flights/FlightChip";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { timeScales } from "../TimelineUtils";
// form Components
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
// Material-UI
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Button, Grid, Icon, Theme, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { TextField } from "src/components/FormikComponents/TextField";
import { IconButton } from "src/components/IconButton/IconButton";
import { updateUserAppSettings } from "src/redux/actions/App.Actions";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";

interface ITimelineViewScale {
  selectedEvents: (IFlight | IGroundEvent)[];
  setFocusOnEvent: (selectedEvent: IFlight | IGroundEvent) => void;
  setFocusOnDate: (date?: Date) => void;
  setViewportScale: (scale?: string | number[]) => void;
  startDate: Date;
  endDate: Date;
  setTabValue?: React.Dispatch<React.SetStateAction<string | boolean>>;
  groupSave?: boolean;
}

type FocusMode = "reset" | "date" | "group" | "individual";

const TimelineFocusStyles = (theme: Theme): any => ({
  display: "flex",
  padding: "10px",
  flexWrap: "wrap",
  "& .controls": {
    "& .focus_button": { margin: "0 5px" },
    "& .follow_current_time_checkbox, & .save_as_default_filter": {
      width: "auto",
      display: "inline-flex",
      "& .label, & label": {
        ...theme.typography.subtitle1,
        width: "auto",
        lineHeight: "14px",
      },
    },
    "& .offset_hours": {
      width: "auto",
      minWidth: "auto",
      "& .input_control": {
        "& label": {
          ...theme.typography.subtitle1,
          width: "auto",
          marginBottom: "5px",
        },
        "& .input-base": {
          width: 60,
          maxHeight: 25,
          margin: "5px",
        },
      },
    },
  },
  "& .focus_mode": {
    display: "flex",
    marginTop: "5px",
    "& .focus_date, & .cycle_through_individual": {
      display: "flex",
      alignItems: "center",
      "& .focus_prev, & .focus_next": { padding: "5px" },
      "& .focus_help_text, & .no_flights_selected": {
        color: theme.custom.forms.labels,
        userSelect: "none",
      },
      "& .flights_amount": {
        color: theme.palette.text.primary,
      },
    },
  },
});

/**
 * TIMELINE FOCUS EVENT(s) TOOL
 * The buttons in this tool are designed to help the user get the best possible view (in the timeline) of events they have selected.
 * It allows them to sequential jump to selected events (without changing timeline scale) as well as zooming into the selections (individually or as a group)
 */
export const TimelineFocus = forwardRef((props: ITimelineViewScale, ref: any): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { setTabValue, groupSave } = props;
  const totalSelectedCount: number = props.selectedEvents ? props.selectedEvents.length : 0;

  const userAppSettings = useSelector((state: IRootState) => state.authentication.appSettings);
  const savedFollowCurrentDate = userAppSettings?.flightTimeline?.followCurrentDate;
  const savedCurrentDateOffsetHours = userAppSettings?.flightTimeline?.currentDateOffsetHours;
  const userSetScale = useSelector((state: IRootState) => state.timelineFlights.userSetScale);

  const [focusDate, setFocusDate] = React.useState<string>(null);
  const [focusMode, setFocusMode] = React.useState<FocusMode>("reset");
  const [displayFlightIndex, setDisplayFlightIndex] = React.useState<number>(null);
  const [activeFlight, setActiveFlight] = React.useState<IFlight | IGroundEvent>(null);
  const [followCurrentDate, setFollowCurrentDate] = React.useState<boolean>(savedFollowCurrentDate);
  const [offsetHours, setOffsetHours] = React.useState<number>(savedCurrentDateOffsetHours);
  const { selectedEvents, setFocusOnEvent } = props;

  useEffect(() => {
    const index = displayFlightIndex - 1;
    if (index > -1 && index <= totalSelectedCount && selectedEvents[index] !== activeFlight) {
      setActiveFlight(selectedEvents[index]);
      setFocusOnEvent(selectedEvents[index]);
    }
  }, [displayFlightIndex, selectedEvents, totalSelectedCount, setFocusOnEvent, activeFlight]);

  // Will test the date output to make sure it's a valid date object before returning a formatted string.
  const renderSelectedDate = (): string => {
    if (!focusDate) {
      return null;
    }
    const newDate = new Date(focusDate);
    if (typeof newDate === "object" && typeof newDate.getMonth === "function") {
      return newDate.toUTCString().split(" ").slice(0, 4).join(" ");
    }
    return null;
  };

  // RESET FOCUS - to default set when tool was opened.
  const resetFocus = (): void => {
    props.setViewportScale(userSetScale);
  };

  /** FOCUS DATE
   * If a date if provided, will jump to selected date on the timeline and focus in on it.
   * If no date is provided, will focus on the current date.
   * @param date -
   */
  const focusOnDate = (date?: string | number): void => {
    if (!date) {
      const currentDate = new Date();
      // Set to midday so that entire day is centered on screen but 'current time bar' is still visible.
      currentDate.setUTCHours(12, 0, 0, 0);
      const currentISO = currentDate.toISOString();
      setFocusDate(currentISO);
      props.setFocusOnDate(currentDate);
    } else {
      setFocusDate(date as string);
      // Add 12 hours to the Date so that the calculated time is centered on screen.
      const jumpDate = (date as string).replace("T00", "T12");
      props.setFocusOnDate(new Date(jumpDate));
    }
    setFocusMode("date");
  };

  // FOCUS ON SELECTED GROUP
  const focusOnGroup = (): void => {
    setDisplayFlightIndex(null);
    if (!props.selectedEvents || totalSelectedCount < 1) {
      return;
    }

    const earliestDep = props.selectedEvents.reduce(
      (eventA: IFlight | IGroundEvent, eventB: IFlight | IGroundEvent) => {
        const Atime = isFlight(eventA) ? new Date(eventA.actualOff) : new Date(eventA.actualStart);
        const Btime = isFlight(eventB) ? new Date(eventB.actualOff) : new Date(eventB.actualStart);
        return Atime < Btime ? eventA : eventB;
      }
    );

    const latestArr = props.selectedEvents.reduce((eventA: IFlight | IGroundEvent, eventB: IFlight | IGroundEvent) => {
      const Atime = isFlight(eventA) ? new Date(eventA.actualOn) : new Date(eventA.actualEnd);
      const Btime = isFlight(eventB) ? new Date(eventB.actualOn) : new Date(eventB.actualEnd);
      return Atime > Btime ? eventA : eventB;
    });

    const startTime = isFlight(earliestDep)
      ? new Date(earliestDep.actualOff).getTime() - timeScales.hour
      : new Date(earliestDep.actualStart).getTime() - timeScales.hour;

    const endTime = isFlight(latestArr)
      ? new Date(latestArr.actualOn).getTime() + timeScales.hour
      : new Date(latestArr.actualEnd).getTime() + timeScales.hour;

    props.setViewportScale([startTime, endTime]);
    setFocusMode("group");
  };

  // ACTIVATE FOCUS INDIVIDUAL MODE - allows users to jump from one flight in selection to another in sequence.
  const individualMode = (): void => {
    setFocusMode("individual");
    setDisplayFlightIndex(1);
  };

  const saveAsMyDefault = useCallback(async () => {
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Saving Follow Date Selections as Default...",
      successMessage: "Follow Date Selections Saved Successfully.",
      errorMessage: "Error: Unable to Save Follow Date Selections",
    };
    const response = await AmplifyAPIWrapper.Put<any>(
      "/me/settings",
      {
        flightTimeline: {
          followCurrentDate: followCurrentDate,
          currentDateOffsetHours: offsetHours,
        },
      },
      !groupSave && notifications
    );
    dispatch(updateUserAppSettings(response, "followCurrentDate"));
  }, [dispatch, followCurrentDate, offsetHours, groupSave]);

  useEffect(() => {
    setFollowCurrentDate(savedFollowCurrentDate);
    setOffsetHours(savedCurrentDateOffsetHours);
  }, [savedCurrentDateOffsetHours, savedFollowCurrentDate]);

  useImperativeHandle(ref, () => ({
    saveDefault() {
      return {
        followCurrentDate: followCurrentDate,
        currentDateOffsetHours: offsetHours,
      };
    },
    refresh() {
      setFollowCurrentDate(savedFollowCurrentDate);
      setOffsetHours(savedCurrentDateOffsetHours);
    },
  }));

  const renderFocusMode = (): JSX.Element => {
    switch (focusMode) {
      case "reset":
        return null;
      case "date":
        return (
          <Grid item className="focus_date">
            <DateTimeField
              className="focus_date"
              name="focusDate"
              label="Focus Date"
              value={focusDate}
              onChange={focusOnDate}
              hideTimeControl={true}
            />
            <Typography variant="subtitle1" className="focus_help_text">
              {renderSelectedDate()}
            </Typography>
          </Grid>
        );
      case "group":
        return (
          <Typography variant="subtitle1" className="focus_help_text">
            If you cannot see the flights selected in the timeline, they may be scrolled out of view.
          </Typography>
        );
      case "individual":
        return (
          <Grid item className="cycle_through_individual">
            {!!activeFlight && !!activeFlight._id && !!activeFlight.aircraft && (
              <div className="flight_chip">
                <FlightChip data={activeFlight as IFlight} />
              </div>
            )}
            <IconButton
              id="focus_prev_event_btn"
              disabled={displayFlightIndex === 1}
              className="focus_prev"
              onClick={() => setDisplayFlightIndex(displayFlightIndex - 1)}
              size="large"
            >
              <Icon>keyboard_arrow_left</Icon>
            </IconButton>
            <IconButton
              id="focus_next_event_btn"
              disabled={displayFlightIndex === totalSelectedCount}
              className="focus_next"
              onClick={() => setDisplayFlightIndex(displayFlightIndex + 1)}
              size="large"
            >
              <Icon>keyboard_arrow_right</Icon>
            </IconButton>
            <Typography variant="subtitle1" className="focus_help_text">
              Focused on flight <span className="flights_amount">{displayFlightIndex}</span> of{" "}
              <span className="flights_amount">{totalSelectedCount}</span> selected.
            </Typography>
          </Grid>
        );
      default:
        return null;
    }
  };

  /**
   * I believe that it is just a firefox issue that the text field of type number
   * allows letters etc so I just added this to the field
   * @param e- event
   */
  const inpNum = (e: any): void => {
    const charCode = typeof e?.which == "undefined" ? e?.keyCode : e?.which;
    const charStr = String.fromCharCode(charCode);
    if (!/^[-0-9]+$/.exec(charStr)) e.preventDefault();
  };

  return (
    <Box sx={TimelineFocusStyles(theme)}>
      <Grid container direction="column">
        <Grid container className="controls" alignItems="center">
          {setTabValue && (
            <IconButton style={{ paddingBottom: "4px" }} onClick={() => setTabValue(false)}>
              <VisibilityOffIcon fontSize="small" />
            </IconButton>
          )}
          {!groupSave && (
            <>
              <Typography variant="subtitle1" className="body_text">
                Focus
              </Typography>
              <Button
                id={"set_date_focus_btn"}
                className="focus_button"
                variant={focusMode === "date" ? "contained" : "outlined"}
                onClick={() => focusOnDate()}
              >
                Date
              </Button>
              <Button
                id={"set_group_focus_btn"}
                className="focus_button"
                variant={focusMode === "group" ? "contained" : "outlined"}
                disabled={!totalSelectedCount}
                onClick={() => focusOnGroup()}
              >
                Selection Group({totalSelectedCount})
              </Button>
              <Button
                id={"set_individual_focus_btn"}
                className="focus_button"
                variant={focusMode === "individual" ? "contained" : "outlined"}
                disabled={!totalSelectedCount}
                onClick={() => individualMode()}
              >
                Individual Selections
              </Button>
            </>
          )}
          <CheckboxField
            style={{ display: "inline-flex", width: "inherit" }}
            className="follow_current_time_checkbox"
            label={"Follow Current Time"}
            name="followCurrentTime"
            // checked={followCurrentDate}
            value={followCurrentDate}
            onChange={() => {
              setFollowCurrentDate(!followCurrentDate);
            }}
          />
          {followCurrentDate && (
            <TextField
              name="textfield_offset_hours"
              className="textfield offset_hours"
              label="Current Date Offset Hours (+/-)"
              type="number"
              onKeyPress={inpNum}
              onChange={(event: any) => {
                //min of -24, max of 24
                const offsetHours = event?.target?.value
                  ? event?.target?.value > 0
                    ? Math.min(event?.target?.value, 24)
                    : Math.max(event?.target?.value, -24)
                  : null;
                setOffsetHours(offsetHours);
              }}
              // defaultValue={0} default value should not be set when value is assigned (see controlled vs. uncontrolled input) - CC
              value={offsetHours ?? 0}
            />
          )}
          {!groupSave && (
            <Button
              id={"deselect_btn"}
              disabled={savedCurrentDateOffsetHours === offsetHours && savedFollowCurrentDate === followCurrentDate}
              variant="outlined"
              onClick={saveAsMyDefault}
              className="save_as_default_filter"
            >
              {"Save as my default"}
            </Button>
          )}
        </Grid>
        <Grid container direction="row" className="focus_mode">
          {renderFocusMode()}
        </Grid>
      </Grid>
    </Box>
  );
});
